/**
 * Wizard Context and State Management
 *
 * Provides centralized state management for the signup wizard.
 * Handles step navigation, validation, and sessionStorage persistence.
 *
 * WP-AUTH-11: Multi-Step Signup Wizard
 */

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import {
  type WizardState,
  initialWizardState,
  saveWizardState,
  loadWizardState,
  clearWizardState,
  clearOAuthConsent,
} from '@/utils/wizardStorage';

/**
 * Extended state with password fields (not persisted)
 */
interface WizardContextState extends WizardState {
  // Password fields - NOT persisted to storage
  password: string;
  confirmPassword: string;
  captchaToken: string | null;

  // UI state
  isSubmitting: boolean;
  error: string | null;
}

/**
 * Wizard actions
 */
type WizardAction =
  | { type: 'SET_STEP'; step: 1 | 2 | 3 }
  | { type: 'UPDATE_STATE'; data: Partial<WizardContextState> }
  | { type: 'MARK_TERMS_REVIEWED' }
  | { type: 'MARK_PRIVACY_REVIEWED' }
  | { type: 'SET_TERMS_ACCEPTED'; accepted: boolean }
  | { type: 'SET_GDPR_CONSENT'; consent: boolean }
  | { type: 'SET_MARKETING_CONSENT'; consent: boolean }
  | { type: 'SET_SELECTED_METHOD'; method: 'email' | 'google' | 'microsoft' | 'apple' | null }
  | { type: 'SET_CREDENTIALS'; name: string; email: string }
  | { type: 'SET_PASSWORD'; password: string }
  | { type: 'SET_CONFIRM_PASSWORD'; confirmPassword: string }
  | { type: 'SET_CAPTCHA_TOKEN'; token: string | null }
  | { type: 'SET_SUBMITTING'; isSubmitting: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'RESET' }
  | { type: 'RESTORE_STATE'; state: WizardState };

const initialContextState: WizardContextState = {
  ...initialWizardState,
  password: '',
  confirmPassword: '',
  captchaToken: null,
  isSubmitting: false,
  error: null,
};

function wizardReducer(
  state: WizardContextState,
  action: WizardAction
): WizardContextState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.step };

    case 'UPDATE_STATE':
      return { ...state, ...action.data };

    case 'MARK_TERMS_REVIEWED':
      return { ...state, termsReviewed: true };

    case 'MARK_PRIVACY_REVIEWED':
      return { ...state, privacyReviewed: true };

    case 'SET_TERMS_ACCEPTED':
      return {
        ...state,
        termsAccepted: action.accepted,
        consentTimestamp: action.accepted ? new Date().toISOString() : null,
      };

    case 'SET_GDPR_CONSENT':
      return { ...state, gdprConsent: action.consent };

    case 'SET_MARKETING_CONSENT':
      return { ...state, marketingConsent: action.consent };

    case 'SET_SELECTED_METHOD':
      return { ...state, selectedMethod: action.method };

    case 'SET_CREDENTIALS':
      return { ...state, name: action.name, email: action.email };

    case 'SET_PASSWORD':
      return { ...state, password: action.password };

    case 'SET_CONFIRM_PASSWORD':
      return { ...state, confirmPassword: action.confirmPassword };

    case 'SET_CAPTCHA_TOKEN':
      return { ...state, captchaToken: action.token };

    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.isSubmitting };

    case 'SET_ERROR':
      return { ...state, error: action.error };

    case 'RESET':
      return initialContextState;

    case 'RESTORE_STATE':
      return {
        ...initialContextState,
        ...action.state,
        // Don't restore password fields
        password: '',
        confirmPassword: '',
        captchaToken: null,
      };

    default:
      return state;
  }
}

/**
 * Context interface
 */
interface WizardContextValue {
  state: WizardContextState;

  // Navigation
  setStep: (step: 1 | 2 | 3) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  canProceedToStep: (step: number) => boolean;

  // Step 1 actions
  markTermsReviewed: () => void;
  markPrivacyReviewed: () => void;
  setTermsAccepted: (accepted: boolean) => void;
  setGdprConsent: (consent: boolean) => void;
  setMarketingConsent: (consent: boolean) => void;

  // Step 2 actions
  setSelectedMethod: (method: 'email' | 'google' | 'microsoft' | 'apple' | null) => void;

  // Step 3 actions
  setCredentials: (name: string, email: string) => void;
  setPassword: (password: string) => void;
  setConfirmPassword: (confirmPassword: string) => void;
  setCaptchaToken: (token: string | null) => void;

  // UI actions
  setSubmitting: (isSubmitting: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  resetWizard: () => void;
}

const WizardContext = createContext<WizardContextValue | null>(null);

/**
 * Provider props
 */
interface WizardProviderProps {
  children: ReactNode;
  /** Initial step from URL query param */
  initialStep?: 1 | 2 | 3;
}

/**
 * Wizard Provider Component
 */
export function WizardProvider({ children, initialStep }: WizardProviderProps) {
  const [state, dispatch] = useReducer(wizardReducer, initialContextState);

  // Restore state from sessionStorage on mount
  useEffect(() => {
    const savedState = loadWizardState();
    if (savedState) {
      dispatch({ type: 'RESTORE_STATE', state: savedState });

      // If URL has a step param and prerequisites are met, use it
      if (initialStep && canProceedToStepInternal(savedState, initialStep)) {
        dispatch({ type: 'SET_STEP', step: initialStep });
      }
    } else if (initialStep) {
      // No saved state but URL has step - validate and redirect if needed
      dispatch({ type: 'SET_STEP', step: 1 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist state to sessionStorage on changes
  useEffect(() => {
    // Only save non-sensitive data
    const stateToPersist: WizardState = {
      currentStep: state.currentStep,
      termsReviewed: state.termsReviewed,
      privacyReviewed: state.privacyReviewed,
      termsAccepted: state.termsAccepted,
      gdprConsent: state.gdprConsent,
      marketingConsent: state.marketingConsent,
      consentTimestamp: state.consentTimestamp,
      selectedMethod: state.selectedMethod,
      name: state.name,
      email: state.email,
      startedAt: state.startedAt,
      lastActiveAt: state.lastActiveAt,
    };
    saveWizardState(stateToPersist);
  }, [state]);

  // Internal validation function (doesn't use state from closure)
  function canProceedToStepInternal(
    wizardState: WizardState | WizardContextState,
    step: number
  ): boolean {
    if (step <= 1) return true;

    // Step 2 requires Step 1 complete
    if (step === 2) {
      return wizardState.termsAccepted && wizardState.gdprConsent;
    }

    // Step 3 requires Step 2 complete (email method selected)
    if (step === 3) {
      return (
        wizardState.termsAccepted &&
        wizardState.gdprConsent &&
        wizardState.selectedMethod === 'email'
      );
    }

    return false;
  }

  // Navigation
  const setStep = useCallback((step: 1 | 2 | 3) => {
    dispatch({ type: 'SET_STEP', step });
  }, []);

  const goToNextStep = useCallback(() => {
    if (state.currentStep < 3) {
      dispatch({ type: 'SET_STEP', step: (state.currentStep + 1) as 1 | 2 | 3 });
    }
  }, [state.currentStep]);

  const goToPreviousStep = useCallback(() => {
    if (state.currentStep > 1) {
      dispatch({ type: 'SET_STEP', step: (state.currentStep - 1) as 1 | 2 | 3 });
    }
  }, [state.currentStep]);

  const canProceedToStep = useCallback(
    (step: number): boolean => {
      return canProceedToStepInternal(state, step);
    },
    [state]
  );

  // Step 1 actions
  const markTermsReviewed = useCallback(() => {
    dispatch({ type: 'MARK_TERMS_REVIEWED' });
  }, []);

  const markPrivacyReviewed = useCallback(() => {
    dispatch({ type: 'MARK_PRIVACY_REVIEWED' });
  }, []);

  const setTermsAccepted = useCallback((accepted: boolean) => {
    dispatch({ type: 'SET_TERMS_ACCEPTED', accepted });
  }, []);

  const setGdprConsent = useCallback((consent: boolean) => {
    dispatch({ type: 'SET_GDPR_CONSENT', consent });
  }, []);

  const setMarketingConsent = useCallback((consent: boolean) => {
    dispatch({ type: 'SET_MARKETING_CONSENT', consent });
  }, []);

  // Step 2 actions
  const setSelectedMethod = useCallback(
    (method: 'email' | 'google' | 'microsoft' | 'apple' | null) => {
      dispatch({ type: 'SET_SELECTED_METHOD', method });
    },
    []
  );

  // Step 3 actions
  const setCredentials = useCallback((name: string, email: string) => {
    dispatch({ type: 'SET_CREDENTIALS', name, email });
  }, []);

  const setPassword = useCallback((password: string) => {
    dispatch({ type: 'SET_PASSWORD', password });
  }, []);

  const setConfirmPassword = useCallback((confirmPassword: string) => {
    dispatch({ type: 'SET_CONFIRM_PASSWORD', confirmPassword });
  }, []);

  const setCaptchaToken = useCallback((token: string | null) => {
    dispatch({ type: 'SET_CAPTCHA_TOKEN', token });
  }, []);

  // UI actions
  const setSubmitting = useCallback((isSubmitting: boolean) => {
    dispatch({ type: 'SET_SUBMITTING', isSubmitting });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', error });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', error: null });
  }, []);

  const resetWizard = useCallback(() => {
    clearOAuthConsent();
    clearWizardState();
    dispatch({ type: 'RESET' });
  }, []);

  const value: WizardContextValue = {
    state,
    setStep,
    goToNextStep,
    goToPreviousStep,
    canProceedToStep,
    markTermsReviewed,
    markPrivacyReviewed,
    setTermsAccepted,
    setGdprConsent,
    setMarketingConsent,
    setSelectedMethod,
    setCredentials,
    setPassword,
    setConfirmPassword,
    setCaptchaToken,
    setSubmitting,
    setError,
    clearError,
    resetWizard,
  };

  return (
    <WizardContext.Provider value={value}>{children}</WizardContext.Provider>
  );
}

/**
 * Hook to access wizard context
 */
export function useWizard(): WizardContextValue {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
}

export default WizardContext;
